import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Layers, Info, RefreshCw, Search, CheckCircle2,
    AlertCircle, Circle, Building
} from 'lucide-react';
import type { FilterStatus, ModelFilter } from './types';

interface AdminBackgroundStatsProps {
    aggregateStats: {
        totalBooks: number;
        completeBooks: number;
        partialBooks: number;
        emptyBooks: number;
        overallCompletion: number;
        overallProPercentage: number;
    };
    model: string;
    setModel: (model: string) => void;
    showCostDetails: boolean;
    setShowCostDetails: (show: boolean) => void;
    isLoadingCounts: boolean;
    onRefreshCounts: (force?: boolean) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    filterStatus: FilterStatus;
    setFilterStatus: (status: FilterStatus) => void;
    modelFilter: ModelFilter;
    setModelFilter: (filter: ModelFilter) => void;
}

export const AdminBackgroundStats = ({
    aggregateStats,
    model,
    setModel,
    showCostDetails,
    setShowCostDetails,
    isLoadingCounts,
    onRefreshCounts,
    searchQuery,
    setSearchQuery,
    filterStatus,
    setFilterStatus,
    modelFilter,
    setModelFilter
}: AdminBackgroundStatsProps) => {
    return (
        <>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Book & Passage Background Data
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    Generate background data for specific books and passages
                </p>
            </CardHeader>

            <CardContent className="space-y-6">
                {/* Controls Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Model:</span>
                        <Select value={model} onValueChange={setModel}>
                            <SelectTrigger className="w-full sm:w-56">
                                <SelectValue placeholder="Select model" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                                <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                                <SelectItem value="gemini-3-flash-preview">Gemini 3.0 Flash</SelectItem>
                                <SelectItem value="gemini-3-pro-preview">Gemini 3.0 Pro</SelectItem>
                                <SelectItem value="gemini-3.1-pro-preview">Gemini 3.1 Pro</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowCostDetails(!showCostDetails)}
                            className="text-xs"
                        >
                            <Info className="h-3 w-3 mr-1" />
                            <span className="hidden sm:inline">{showCostDetails ? 'Hide' : 'Show'} Cost Info</span>
                            <span className="sm:hidden">Cost</span>
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onRefreshCounts(true)}
                            disabled={isLoadingCounts}
                        >
                            {isLoadingCounts ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                        </Button>
                    </div>
                </div>

                {/* Cost Details (Collapsible) */}
                {showCostDetails && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg text-sm border border-blue-200 dark:border-blue-900">
                        <p className="font-medium mb-2">💰 Cost Estimates (Gemini 2.5 Flash per book):</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
                            <div>• Cultural (Book): ~$0.0008</div>
                            <div>• Archaeological (Book): ~$0.0008</div>
                            <div>• Cultural (Passages): ~$0.023</div>
                            <div>• Archaeological (Passages): ~$0.018</div>
                            <div>• Historical (Book): ~$0.0008</div>
                            <div>• Geographic (Passages): ~$0.023</div>
                            <div>• Historical (Passages): ~$0.018</div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            <strong>Note:</strong> Passage costs assume ~45 passages/book average. Actual costs may vary.
                        </p>
                    </div>
                )}

                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <Card className="border-2">
                        <CardContent className="pt-4 pb-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold">{aggregateStats.completeBooks.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">Complete</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-2">
                        <CardContent className="pt-4 pb-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold">{aggregateStats.partialBooks.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">Partial</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-2">
                        <CardContent className="pt-4 pb-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold">{aggregateStats.emptyBooks.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">Empty</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-2 border-primary/20">
                        <CardContent className="pt-4 pb-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-primary">{aggregateStats.overallProPercentage.toFixed(0)}%</div>
                                <div className="text-xs text-muted-foreground font-medium">Overall Pro</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Search and Filter */}
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search books..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as FilterStatus)}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Books</SelectItem>
                            <SelectItem value="complete">Complete</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            <SelectItem value="empty">Empty</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={modelFilter} onValueChange={(value) => setModelFilter(value as ModelFilter)}>
                        <SelectTrigger className="w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Models</SelectItem>
                            <SelectItem value="2.5-flash">2.5-Flash Only</SelectItem>
                            <SelectItem value="3-flash">3-Flash Only</SelectItem>
                            <SelectItem value="pro">Pro Only</SelectItem>
                            <SelectItem value="mixed">Mixed Models</SelectItem>
                            <SelectItem value="needs-upgrade">Needs Upgrade</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                    <span className="font-medium">Legend:</span>
                    <div className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        <span>Book-level</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Layers className="h-3 w-3" />
                        <span>Passage-level</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        <span>Complete</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-orange-600" />
                        <span>Partial</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Circle className="h-3 w-3" />
                        <span>Empty</span>
                    </div>
                </div>
            </CardContent>
        </>
    );
};
